import { Card, CardContent, CardHeader, CardTitle } from '@agentselly/ui';
import { FileText, ArrowRight, Download } from 'lucide-react';
import { useTranslate } from '@tolgee/react';
import { AppLink } from '~/components/AppLink';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useAppParams } from '~/helper/useAppParams';

interface Document {
  readonly id: string;
  readonly type: string;
  readonly title: string;
  readonly filename: string;
  readonly url: string;
  readonly thumbnail?: string;
  readonly isPrimary?: boolean;
}

interface CockpitDocumentsDashboardWidgetProps {
  readonly title?: string;
  readonly documents?: Document[];
  readonly infoText?: string;
}

export function CockpitDocumentsDashboardWidget(
  props: CockpitDocumentsDashboardWidgetProps,
) {
  const { t } = useTranslate();
  const params = useAppParams();

  const documents = props.documents || [];
  const primaryDocs = documents.filter((d) => d.isPrimary);
  const otherDocs = documents.filter((d) => !d.isPrimary);

  console.log('CockpitDocumentsDashboardWidget', {
    props,
    documents,
    primaryDocs,
    otherDocs,
  });

  const documentsRoute =
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS
      .replace('$lang', params.lang)
      .replace('$companyId', params.companyId)
      .replace('$contactId', params.contactId)
      .replace('$propertyId', params.propertyId);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            {props.title || t('cockpit_documents_dashboard_widget_title')}
          </CardTitle>
          <AppLink
            to={documentsRoute}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors"
          >
            {t('cockpit_documents_dashboard_widget_view_all')}
            <ArrowRight className="h-4 w-4" />
          </AppLink>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {documents.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <FileText className="mx-auto h-12 w-12 mb-2 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
            <p className="text-sm">
              {t('cockpit_documents_dashboard_widget_no_documents')}
            </p>
            <AppLink
              to={documentsRoute}
              className="mt-3 inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
            >
              {t('cockpit_documents_dashboard_widget_upload_first')}
              <ArrowRight className="h-4 w-4 ml-1" />
            </AppLink>
          </div>
        ) : (
          <div className="space-y-4">
            {primaryDocs.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {primaryDocs.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative aspect-[3/4] rounded-lg overflow-hidden border-2 border-primary-200 hover:border-primary-400 transition-all hover:shadow-md dark:hover:shadow-gray-900/50 bg-gradient-to-br from-primary-50 to-primary-100"
                  >
                    <div className="w-full h-full flex flex-col items-center justify-center p-4">
                      <div className="w-20 h-20 mb-3 rounded-lg bg-white dark:bg-gray-800/80 dark:bg-gray-700/80 flex items-center justify-center shadow-sm dark:shadow-gray-900/50">
                        <FileText className="h-12 w-12 text-primary-600" />
                      </div>
                      <p className="font-semibold text-sm text-center text-primary-900 mb-1">
                        {doc.title}
                      </p>
                      <p className="text-xs text-primary-600 text-center truncate w-full px-2">
                        {doc.filename}
                      </p>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-600/90 via-primary-600/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <Download className="h-8 w-8 mb-2 drop-shadow-md" />
                      <p className="font-semibold text-sm drop-shadow-md">
                        {t('cockpit_documents_dashboard_widget_download')}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {otherDocs.length > 0 && (
              <div className="space-y-2">
                {primaryDocs.length > 0 && (
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 dark:text-gray-300 mt-4">
                    {t('cockpit_documents_dashboard_widget_other_documents')}
                  </h4>
                )}
                <div className="space-y-1">
                  {otherDocs.slice(0, 3).map((doc) => (
                    <a
                      key={doc.id}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800 dark:bg-gray-800 dark:hover:bg-gray-800 transition-colors group"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-200 dark:text-gray-300 truncate">
                          {doc.title}
                        </span>
                      </div>
                      <Download className="h-4 w-4 text-gray-400 dark:text-gray-500 dark:text-gray-400 group-hover:text-primary-600 flex-shrink-0 transition-colors" />
                    </a>
                  ))}
                  {otherDocs.length > 3 && (
                    <AppLink
                      to={documentsRoute}
                      className="flex items-center justify-center p-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
                    >
                      {t('cockpit_documents_dashboard_widget_more_documents', {
                        count: otherDocs.length - 3,
                      })}
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </AppLink>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
